import { ColumnSeries, ValueAxis, XYChart, LabelBullet, CategoryAxis, LineSeries, Bullet } from "@amcharts/amcharts4/charts";
import { color, create, NumberFormatter, percent, Circle, Line } from "@amcharts/amcharts4/core";
import _ from "lodash";
import React, { Component, Dispatch, SetStateAction } from "react";

import WebFont from "webfontloader";
import "../../../assets/styles/component/legend.scss";
import { BLACK, RED, GRAY, WHITE } from "../../../constants";
import LoadingEllipsis from "../../Loading/LoadingEllipsis";
import { disposeChart } from "../helpers/chartHelpers";
import { configureValueAxis, configureCategoryAxis } from "../helpers/configurations";
import { oceChartConfiguration } from "../helpers/oceConfigurations";
import { ThemeInt } from "../helpers/types";

interface Props {
  data: any;
  granularity: string;
  theme: ThemeInt;
  chartId: string;
  // seriesList: { name: string; field: string; hide: boolean; colour?: string }[];
  percentageChart?: boolean;
  nissanCompetitor?: string;
  market: string;
  diff: number;
  legendPosition?: "right" | "left" | "top" | "bottom";
  setIsLoadingChartSortingLogic?: Dispatch<SetStateAction<boolean>>;
}

interface ChartData {
  date: string;
  score: string;
  brand: string;
  market: string;
  nissan_score: string;
}

class BrandKPISummaryBarGraph extends Component<Props> {
  constructor(props: Props) {
    super(props);
  }

  chart!: XYChart;
  chartId = this.props.chartId;

  componentDidMount() {
    WebFont.load({
      custom: {
        families: ["nissan"],
        urls: ["../../../assets/fonts/fonts.css"],
      },
      // @ts-ignore
      active: this.initChart(),
      timeout: 8000,
    });
  }

  componentDidUpdate(prevProps: any) {
    //Handle refreshing the chart when the dataset changes
    if (!_.isEqual(prevProps, this.props)) {
      disposeChart(this.chartId).then((res) => {
        this.initChart();
      });
    }
  }

  componentWillUnmount() {
    disposeChart(this.chartId);
  }

  initChart() {
    const { data, theme, setIsLoadingChartSortingLogic } = this.props;
    const transformData = (data: { date: string; market: string; scores: string[]; brands: string[]; nissan_score: string }[]) => {
      return data?.map((item) => {
        setIsLoadingChartSortingLogic && setIsLoadingChartSortingLogic(true);
        const transformedItem: { [key: string]: any } = {
          date: item.date,
          market: item.market,
          nissan_score: item.nissan_score,
        };

        item.scores.forEach((score: string, index: number) => {
          transformedItem[`score_${index + 1}`] = score;
          transformedItem[`brand_${index + 1}`] = item.brands[index];
        });

        setIsLoadingChartSortingLogic && setIsLoadingChartSortingLogic(false);

        return transformedItem as ChartData;
      });
    };

    this.chart = create(this.chartId, XYChart);
    oceChartConfiguration(this.chart);
    this.chart.data = transformData(data);

    this.chart.numberFormatter.numberFormat = "#.0a";
    this.chart.paddingRight = 50;
    this.chart.responsive.enabled = true;

    // Create Axes
    const categoryAxis = this.chart.yAxes.push(new CategoryAxis());
    configureCategoryAxis(categoryAxis, theme);
    categoryAxis.dataFields.category = "market";
    categoryAxis.renderer.grid.template.location = 0;
    // categoryAxis.renderer.minGridDistance = 40;
    categoryAxis.renderer.inversed = true;
    categoryAxis.renderer.labels.template.disabled = true;

    const valueAxis = this.chart.xAxes.push(new ValueAxis());
    configureValueAxis(valueAxis, theme);
    // valueAxis.renderer.opposite = true;
    valueAxis.min = 0;
    valueAxis.numberFormatter = new NumberFormatter();
    valueAxis.numberFormatter.numberFormat = "#a";
    valueAxis.paddingRight = 30;
    valueAxis.renderer.labels.template.disabled = true;
    valueAxis.renderer.grid.template.disabled = true;

    const scoresCount = data[0]?.scores?.length || 0;

    const createSeries = (chart: XYChart, field: string, name: string, brandField: string, nissan_score: string) => {
      const series = chart.series.push(new ColumnSeries());
      series.dataFields.valueX = field;
      series.dataFields.categoryY = "market";
      series.columns.template.tooltipText = `[bold; font-size: var(--regular_font_size);]{categoryY}-${brandField}: {valueX.formatNumber("#.0a")}%`;
      // reduce size
      // series.columns.template.height = percent(50);
      if (series.tooltip) {
        series.tooltip.autoTextColor = false;
        series.tooltip.label.fill = color(WHITE);
      }

      if (brandField === "Nissan") {
        series.columns.template.fill = color(RED);
        series.columns.template.stroke = color(RED);
      } else {
        series.columns.template.fill = color(GRAY);
        series.columns.template.stroke = color(GRAY);
      }

      const labelBullet = series.bullets.push(new LabelBullet());
      labelBullet.label.paddingLeft = 5;
      // labelBullet.label.text = `{valueX.formatNumber("#.0a")}%`;
      labelBullet.label.fill = theme === "light" ? color(BLACK) : color(WHITE);
      labelBullet.label.horizontalCenter = "left";
      labelBullet.label.truncate = false;

      const range = valueAxis.createSeriesRange(series);
      range.value = Number(nissan_score);
      range.endValue = Number(nissan_score) + 0.3;
      range.contents.stroke = color(RED);
      range.contents.fill = range.contents.stroke;

      // const lineSeries = chart.series.push(new LineSeries());
      // lineSeries.dataFields.categoryY = "market";
      // lineSeries.dataFields.valueX = "nissan_score";
      // lineSeries.stroke = color(RED);
      // lineSeries.name = "Nissan Score";
      // lineSeries.tooltipText = "{valueX}";
      // lineSeries.strokeWidth = 2;

      // // Add a bullet to the line series
      // const bullet = lineSeries.bullets.push(new Bullet());
      // const line = bullet.createChild(Line);
      // line.stroke = color(RED);
      // line.strokeWidth = 1;
      // line.height = percent(150);

      // const circleBullet = lineSeries.bullets.push(new CircleBullet());
      // circleBullet.circle.fill = color("#fff");
      // circleBullet.circle.strokeWidth = 2;
      // circleBullet.height = percent(100);
    };

    for (let i = 1; i <= scoresCount; i++) {
      createSeries(this.chart, `score_${i}`, `Score ${i}`, `${data[0]?.brands[i - 1]}`, data[0]?.nissan_score);
    }
  }

  render() {
    return (
      <>
        <div className={"chartWrapper"}>
          <div id={this.props.chartId} data-test-id={this.props.chartId} className={"graph"} />
        </div>
      </>
    );
  }
}

export default BrandKPISummaryBarGraph;
